import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { User } from '@e-origin/shared';

import { useCreateUser, useEditUser } from '@hooks/user';
import { passwordSchema } from '@validators';

const createUserSchema = z.object({
  email: z.string().email({ message: 'Email address is required' }),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  password: passwordSchema,
});

const editUserSchema = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  deleted: z.boolean().optional(),
  visible: z.boolean().optional(),
});

export const useEditUserFormInitializer = ({ user }) => {
  const createUserMutation = useCreateUser();
  const editUserMutation = useEditUser();

  const onSubmit = async (values) => {
    if (values._id) {
      return await editUserMutation.mutateAsync({
        userId: values._id,
        userData: {
          firstName: values.firstName,
          lastName: values.lastName,
          deleted: values.deleted,
          visible: values.visible,
        },
      });
    } else {
      return await createUserMutation.mutateAsync({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
      });
    }
  };

  const buildInitialValues = (user: User | undefined) => ({
    _id: user?._id,
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    deleted: user?.deleted || false,
    visible: user?.visible !== false,
    password: '',
  });

  const form = useForm({
    initialValues: buildInitialValues(user),
    validate: zodResolver(user ? editUserSchema : createUserSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setValues(buildInitialValues(user));
  }, [user]);

  return {
    form,
    onSubmit,
    isSubmitting: createUserMutation.isPending || editUserMutation.isPending,
  };
};
